import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import type { CommonModalProps } from '../../types'

const DEFAULT_TIMEOUT = 0

interface WindowSize {
  width: number
  height: number
}

interface OrientationChangeProps extends Partial<CommonModalProps> {
  timeout?: number
}

interface OrientationChangeResult {
  isOrientationInProgress: boolean
}

export const useOrientationChange = (
  onResize: () => void,
  props?: OrientationChangeProps
): OrientationChangeResult => {
  const timeout = props?.timeout ?? DEFAULT_TIMEOUT

  const [isOrientationInProgress, setOrientationInProgress] = useState<boolean>(false)
  const onResizeRef = useRef(onResize)
  const prevSizeRef = useRef<WindowSize>({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  })

  useLayoutEffect(() => {
    onResizeRef.current = onResize
  }, [onResize])

  useLayoutEffect(() => {
    if (props?.isLoading === false && !isOrientationInProgress) {
      onResizeRef.current()
    }
  }, [props?.isLoading, isOrientationInProgress])

  const handleResize = useCallback(() => {
    setOrientationInProgress(true)

    const resizeTimer = setTimeout(() => {
      onResizeRef.current()
      setOrientationInProgress(false)
    }, timeout)

    return resizeTimer
  }, [timeout])

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    let resizeTimer: ReturnType<typeof setTimeout> | null = null

    const resizeHandler = () => {
      const { innerWidth, innerHeight } = window
      if (innerWidth !== prevSizeRef.current.width || innerHeight !== prevSizeRef.current.height) {
        prevSizeRef.current = {
          width: innerWidth,
          height: innerHeight,
        }
        if (resizeTimer) {
          clearTimeout(resizeTimer)
        }
        resizeTimer = handleResize()
      }
    }

    window.addEventListener('resize', resizeHandler)

    return () => {
      window.removeEventListener('resize', resizeHandler)
      if (resizeTimer) {
        clearTimeout(resizeTimer)
      }
    }
  }, [handleResize])

  return { isOrientationInProgress }
}

export const isPortrait = () => {
  if (window.screen.orientation) {
    return window.screen.orientation.type.startsWith('portrait')
  }
  return window.innerHeight > window.innerWidth
}

export const isLandscape = () => {
  if (window.screen.orientation) {
    return window.screen.orientation.type.startsWith('landscape')
  }
  return window.innerWidth > window.innerHeight
}
